import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

//import './index.scss';

type CorrectScoreProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const CorrectScore = (componentProps: CorrectScoreProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);
  const [state, setState]: any = React.useState({
    types: [
      { label: 'Main', value: 'main' },
      { label: 'All', value: 'all' },
    ],
    selectedType: 'main',

    home: '0',
    away: '0',
  });

  const onSetType = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const tab = event.currentTarget.getAttribute('data-tab');
    if (['main', 'all'].includes(tab || '')) {
      setState((prevState: any) => ({
        ...prevState,
        selectedType: tab || '',
      }));
    }
    event.stopPropagation();
  }, []);

  const onUpdateSide = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const side: string = event.currentTarget.getAttribute('data-side') ?? 'home';
      const changeStr = event.currentTarget.getAttribute('data-change');

      if (!side || !['home', 'away'].includes(side)) return;
      const change = changeStr === '1' ? 1 : changeStr === '-1' ? -1 : 0;
      if (change === 0) return;

      const otherSide = side === 'home' ? 'away' : 'home';

      if (state[side] === '0' && change === -1) return;

      const targetMainValue = parseInt(state[side]) + change;
      const allowedScores: string[] = [];

      props.properties?.market?.outcomes?.forEach((outcome: any) => {
        allowedScores.push(outcome.oddName);
      });

      // 2. Find all valid pairs that have this new value
      const possiblePairs = allowedScores
        .map((s: any) => {
          const [h, a] = s.split(':').map(Number);
          return { home: h, away: a };
        })
        .filter((pair: any) => pair[side] === targetMainValue);

      // 3. If no pairs exist for that number, we stop (e.g., trying to go to Home: 5 when max is 2)
      if (possiblePairs.length === 0) return;

      // 4. Find the pair where the 'other' side is closest to our current 'other' value
      // This prevents the score from jumping wildly (e.g., 0:2 -> 1:0)
      const bestMatch: any = possiblePairs.reduce((prev, curr) => {
        return Math.abs(curr[otherSide] - state[otherSide]) < Math.abs(prev[otherSide] - state[otherSide])
          ? curr
          : prev;
      });

      console.log('CorrectScore[onUpdateSide]', side, change, bestMatch);

      if (bestMatch == null) return;
      setState((prevState: any) => ({
        ...prevState,
        [side]: String(bestMatch[side]),
        [otherSide]: String(bestMatch[otherSide]),
      }));
    },
    [state, props.properties.market],
  );

  const mainHeader = React.useCallback(
    (market: any) => {
      if (!market) return [];
      const columns: any[] = [];

      if (dataElementContext?.parentContext?.match) {
        columns.push({
          label: dataElementContext.parentContext.match.team1Name,
          value: 'home',
        });
        columns.push({
          label: dataElementContext.parentContext.match.team2Name,
          value: 'away',
        });
      }

      columns.push({ label: '', value: 'empty' });

      return columns;
    },
    [dataElementContext],
  );

  const mainOptions = React.useCallback((market: any) => {
    if (!market) return [];
    let homeOptions: any = {};
    let awayOptions: any = {};
    const outcomesMap: any = {};

    market.outcomes?.forEach((outcome: any) => {
      const oddName = outcome.oddName || '';
      const nameParts = oddName.split(':');
      const homePart = nameParts[0]?.trim() || '0';
      const awayPart = nameParts[1]?.trim() || '0';

      homeOptions[homePart] = true;
      awayOptions[awayPart] = true;

      outcomesMap[oddName] = outcome;
    });

    homeOptions = Object.keys(homeOptions)
      .sort((a: any, b: any) => parseInt(a) - parseInt(b))
      .map((score) => {
        return {
          label: score,
          value: score,
        };
      });
    awayOptions = Object.keys(awayOptions)
      .sort((a: any, b: any) => parseInt(a) - parseInt(b))
      .map((score) => {
        return {
          label: score,
          value: score,
        };
      });

    return {
      homeOptions,
      awayOptions,
      outcomesMap,
    };
  }, []);

  const allHeader = React.useCallback(
    (market: any) => {
      if (!market) return [];
      const columns: any[] = [];

      if (dataElementContext?.parentContext?.match) {
        columns.push({
          label: dataElementContext.parentContext.match.team1Name,
          value: 'home',
        });

        columns.push({ label: 'Draw', value: 'draw' });

        columns.push({
          label: dataElementContext.parentContext.match.team2Name,
          value: 'away',
        });
      }

      return columns;
    },
    [dataElementContext],
  );

  const allOutcomes = React.useCallback((market: any) => {
    if (!market) return [];
    const outcomesMap: any = {
      home: [],
      draw: [],
      away: [],
    };

    market.outcomes?.forEach((outcome: any) => {
      const oddName = outcome.oddName || '';
      const nameParts = oddName.split(':');
      const homePart = parseInt(nameParts[0]?.trim() || '0');
      const awayPart = parseInt(nameParts[1]?.trim() || '0');

      if (homePart > awayPart) {
        outcomesMap['home'].push(outcome);
      } else if (homePart === awayPart) {
        outcomesMap['draw'].push(outcome);
      } else {
        outcomesMap['away'].push(outcome);
      }
    });

    ['home', 'draw', 'away'].forEach((side) =>
      outcomesMap[side].sort((a: any, b: any) => {
        const aPart = a.oddName;
        const bPart = b.oddName;
        return aPart.localeCompare(bPart);
      }),
    );

    const outcomesList: any[] = [];

    const maxLength = Math.max(outcomesMap['home'].length, outcomesMap['draw'].length, outcomesMap['away'].length);

    for (let i = 0; i < maxLength; i++) {
      const rowOutcomes: any[] = [];
      ['home', 'draw', 'away'].forEach((side) => {
        if (outcomesMap[side][i]) {
          rowOutcomes.push(outcomesMap[side][i]);
        } else {
          rowOutcomes.push({ label: ' ' });
        }
      });
      outcomesList.push({ outcomes: rowOutcomes });
    }

    return outcomesList;
  }, []);

  const contextValue = React.useMemo(() => {
    const market = props.properties.market;

    const options: any = mainOptions(market);

    return {
      market,
      mainHeader: mainHeader(market),
      homeOptions: options.homeOptions,
      awayOptions: options.awayOptions,
      outcomesMap: options.outcomesMap,
      allHeader: allHeader(market),
      allOutcomes: allOutcomes(market),
      actions: {
        onSetType,
        onUpdateSide,
      },
      ...state,
    };
  }, [props, state, mainHeader, allHeader, allOutcomes, mainOptions, onSetType, onUpdateSide]);

  if (!isVisible) return null;
  //console.log('CorrectScore[dataElementContext]', dataElementContext);
  //console.log('CorrectScore[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default CorrectScore;
